// src/utils/conversation/pendingSelectionSignals.js
// Owns: candidate admission (pre-admissibility)

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s'-]/g, '')
    .replace(/\s+/g, ' ');
}

function isQuestionLike(input = '') {
  const lower = normalizeText(input);

  if (!lower) return false;

  return (
    lower.includes('?') ||
    lower.startsWith('do ') ||
    lower.startsWith('did ') ||
    lower.startsWith('are ') ||
    lower.startsWith('is ') ||
    lower.startsWith('who ') ||
    lower.startsWith('what ') ||
    lower.startsWith('when ') ||
    lower.startsWith('where ') ||
    lower.startsWith('why ') ||
    lower.startsWith('how ')
  );
}

function isGroupReference(input = '') {
  const lower = normalizeText(input);

  return [
    'friends',
    'family',
    'people',
    'coworkers',
    'colleagues',
    'relationships',
    'everyone',
    'anyone',
  ].some((word) => lower.includes(word));
}

function isCourtesyOnly(input = '') {
  const lower = normalizeText(input);

  return [
    'thanks',
    'thank you',
    'thankyou',
    'thx',
    'appreciate it',
    'much appreciated',
    'got it',
    'ok thanks',
    'okay thanks',
    'thanks daisy',
    'thank you daisy',
  ].includes(lower);
}

function declinesSpecificSelection(input = '') {
  const lower = normalizeText(input);

  if (!lower) return false;

  const hasNegation = /\b(no|nah|nope|not|dont|don't|probably not)\b/.test(lower);
  const hasNonSpecificLanguage = /\b(specific|particular|really|anyone|anything|one)\b/.test(lower);
  const hasDeferral = /\b(now|right now|at the moment|for now)\b/.test(lower);

  if (hasNegation && (hasNonSpecificLanguage || hasDeferral)) return true;
  if (hasNegation && lower.split(/\s+/).length <= 4) return true;

  return false;
}

function isValidEntityCandidate(candidate = '') {
  const trimmed = candidate.trim();
  const normalized = normalizeText(trimmed);

  if (!trimmed) return false;

  if (trimmed.split(/\s+/).length > 3) return false;

  if (/^(what|who|where|why|how|when)$/i.test(trimmed)) return false;

  if (normalized.includes('remember')) return false;

  if (isCourtesyOnly(normalized)) return false;

  return /^[A-Za-z][A-Za-z' -]*$/.test(trimmed);
}

function extractPendingSelectionCandidate(input = '') {
  const trimmed = String(input).trim();
  if (!trimmed) return null;

  const normalized = normalizeText(trimmed);

  if (
    isQuestionLike(trimmed) ||
    isGroupReference(trimmed) ||
    isCourtesyOnly(normalized)
  ) {
    return null;
  }

  const copulaMatch = trimmed.match(/^(.+?)\s+(?:is|are|was|were)\b/i);
  if (copulaMatch?.[1]) {
    const candidate = copulaMatch[1].trim();
    if (isValidEntityCandidate(candidate)) return candidate;
  }

  const listMatch = trimmed.match(/^(?:i\s+)?(?:need|want|add|include|put)\s+(.+?)(?:\s+too)?$/i);
  if (listMatch?.[1]) {
    const candidate = listMatch[1].trim();
    if (isValidEntityCandidate(candidate)) return candidate;
  }

  if (isValidEntityCandidate(trimmed)) {
    return trimmed;
  }

  return null;
}

export function getPendingSelectionSignals(input = '') {
  const userInput = String(input).trim();
  const normalizedInput = normalizeText(userInput);

  const greetingLike = [
    'hi',
    'hello',
    'hey',
    'hi daisy',
    'hello daisy',
    'hey daisy',
    'good morning',
    'good afternoon',
    'good evening',
  ].includes(normalizedInput);

  const closureLike = [
    'never mind',
    'nevermind',
    'nah',
    'nope',
    'leave it',
    'leave it there',
    'its ok',
    "it's ok",
    'its okay',
    "it's okay",
  ].includes(normalizedInput);

  const noSpecificSelectionLike = declinesSpecificSelection(userInput);
  const extractedCandidate = extractPendingSelectionCandidate(userInput);

  return {
    userInput,
    normalizedInput,
    greetingLike,
    closureLike,
    noSpecificSelectionLike,
    extractedCandidate,
  };
}